// ── 직원 관리 — 상수 & 헬퍼 함수 ─────────────────────────────────────────────
import { toDateTimeString, isValidYmd } from '../../../utils/date';

export const PAGE_SIZE              = 10;
export const MOBILE_BREAKPOINT      = 768;
export const NO_ID_PREFIX           = 'noid_';
// 기본 출근 유형: 빈 값(NULL) = 출근 없음
export const DEFAULT_ATTENDANCE_TIME_TYPE = '';
export const ATTENDANCE_TIME_TYPE_FALLBACK = ['풀타임', '파트타임'];

// 일괄등록 스프레드시트 열 정의 (key, label, required, placeholder)
export const BULK_COLUMNS = [
  { key: 'name',         label: '성명',     required: true,  placeholder: '홍길동' },
  { key: 'birth_date',   label: '생년월일', required: false, placeholder: '900101' },
  { key: 'address',      label: '주소',     required: false, placeholder: '서울시 강남구 ...' },
  { key: 'phone',        label: '전화번호', required: false, placeholder: '010-0000-0000' },
  { key: 'hire_date',    label: '입사일자', required: true,  placeholder: '2026-03-06' },
  { key: 'department',   label: '부서',     required: true,  placeholder: '마케팅' },
  { key: 'email',        label: '이메일',   required: false, placeholder: 'abc 또는 abc@gmail.com' },
  { key: 'bank_name',    label: '은행명',   required: false, placeholder: '국민은행' },
  { key: 'bank_account', label: '계좌번호', required: false, placeholder: '숫자만' },
  { key: 'resign_date',  label: '퇴사일자', required: false, placeholder: '2026-12-31' },
  { key: 'memo',         label: '비고',     required: false, placeholder: '메모' },
];

// ── 값 검증 헬퍼 ──────────────────────────────────────────────────────────────

/** user_id가 미설정('noid_' 접두사) 여부 */
export function isNoId(userId) {
  return String(userId || '').startsWith(NO_ID_PREFIX);
}

/** 부서명 정규화: 앞뒤 공백 제거 */
export function normalizeDepartmentValue(input) {
  return String(input || '').trim();
}

/** 출근 유형 정규화: 빈 값(NULL=출근없음) 허용, 허용 목록 내 값 그대로 반환 */
export function normalizeAttendanceTimeType(input, options = ATTENDANCE_TIME_TYPE_FALLBACK) {
  const text = String(input || '').trim();
  if (!text) return '';
  const candidates = Array.isArray(options) && options.length > 0 ? options : ATTENDANCE_TIME_TYPE_FALLBACK;
  if (candidates.includes(text)) return text;
  return text; // 알 수 없는 값도 그대로 허용 (서버에서 최종 검증)
}

/** 숫자만 추출 (최대 max 자리) */
export function onlyDigits(input, max = 20) {
  return String(input || '').replace(/\D/g, '').slice(0, max);
}

/** 전화번호 포맷 (010-0000-0000) */
export function formatPhone(input) {
  const digits = onlyDigits(input, 11);
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

/** 날짜 문자열을 YYYY-MM-DD 형식으로 변환 */
export function toDate(v) {
  if (!v) return '';
  const text = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  // toDateTimeString: utils/date.js 사용 (new Date 직접 사용 금지)
  const kstDate = toDateTimeString(text).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(kstDate) ? kstDate : text.slice(0, 10);
}

/** gmail 주소에서 아이디 부분만 추출 */
export function splitEmailId(email) {
  if (!email) return '';
  const lower = String(email).trim().toLowerCase();
  if (!lower.endsWith('@gmail.com')) return '';
  return lower.slice(0, -10);
}

/** 이메일 아이디에 @gmail.com 접미사 추가 */
export function toGmailEmail(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return null;
  if (text.includes('@')) return text;
  return `${text}@gmail.com`;
}

/** 날짜 입력값을 저장용 YYYY-MM-DD 검증 전에 정리 */
export function normalizeDateTextInput(value) {
  return String(value || '')
    .trim()
    .replace(/[./]/g, '-')
    .replace(/[‐‑‒–—―−]/g, '-');
}

/** YYYY-MM-DD 날짜 문자열 유효성 검사 */
export function isValidDateText(text) {
  // isValidYmd: utils/date.js 사용 (new Date 직접 사용 금지)
  return isValidYmd(normalizeDateTextInput(text));
}

// ── 폼 초기값 생성 ───────────────────────────────────────────────────────────

/** 빈 직원 편집 폼 객체 반환 */
export function emptyForm() {
  return {
    userId: '',
    name: '',
    department: '',
    attendance_time_type: DEFAULT_ATTENDANCE_TIME_TYPE,
    original_attendance_time_type: DEFAULT_ATTENDANCE_TIME_TYPE,
    work_schedule_effective_from: '',
    work_schedule_change_mode: 'today',
    work_schedule_reason: '',
    department_effective_from: '',
    department_change_mode: 'today',
    department_change_reason: '',
    phone: '',
    emailId: '',
    birth_date: '',
    addressBase: '',
    addressDetail: '',
    bank_name: '',
    bank_account: '',
    bank_effective_from: '',
    bank_change_mode: 'today',
    bank_change_reason: '',
    hire_date: '',
    resign_date: '',
    first_year_grant_count: '',
    memo: '',
    default_work_location: '',
  };
}

/** 빈 일괄등록 행 객체 반환 */
export function createEmptyBulkRow() {
  return {
    name: '',
    birth_date: '',
    address: '',
    phone: '',
    hire_date: '',
    department: '',
    email: '',
    bank_name: '',
    bank_account: '',
    resign_date: '',
    memo: '',
  };
}

/** 일괄등록 행이 완전히 비어 있는지 여부 */
export function isBulkRowEmpty(row) {
  return BULK_COLUMNS.every((col) => String(row[col.key] || '').trim() === '');
}

// ── 클립보드 붙여넣기 파싱 ─────────────────────────────────────────────────

/** 탭·개행 구분 클립보드 텍스트 → 2차원 배열 변환 */
export function parseClipboardMatrix(rawText) {
  return String(rawText || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => line.length > 0)
    .map((line) => line.split('\t'));
}

/** 일괄등록 셀 값 정규화 (전화번호·날짜 포맷 자동 적용) */
export function normalizeBulkCellValue(key, rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return '';
  if (key === 'birth_date')                          return onlyDigits(value, 6);
  if (key === 'phone')                               return formatPhone(value);
  if (key === 'bank_account')                        return onlyDigits(value, 30);
  if (key === 'email')                               return value.toLowerCase();
  if (key === 'department')                          return normalizeDepartmentValue(value);
  if (key === 'hire_date' || key === 'resign_date')  return normalizeDateTextInput(value);
  return value;
}

/** 일괄등록 셀 유효성 오류 메시지 반환 (없으면 '') */
export function getBulkCellError(row, key, departments = []) {
  const value = String(row[key] || '').trim();

  if (key === 'name') {
    return value ? '' : '성명은 필수입니다.';
  }
  if (key === 'department') {
    if (!value) return '부서는 필수입니다.';
    if (departments.length > 0 && !departments.includes(value)) return '등록된 부서명만 사용 가능합니다.';
    return '';
  }
  if (key === 'birth_date') {
    if (!value) return '';
    return /^\d{6}$/.test(value) ? '' : '생년월일은 6자리 숫자여야 합니다.';
  }
  if (key === 'phone') {
    if (!value) return '';
    return /^01\d-\d{3,4}-\d{4}$/.test(value) ? '' : '전화번호 형식은 010-0000-0000 입니다.';
  }
  if (key === 'hire_date') {
    if (!value) return '입사일자는 필수입니다.';
    return isValidDateText(value) ? '' : '입사일자는 YYYY-MM-DD 형식이어야 합니다.';
  }
  if (key === 'resign_date') {
    if (!value) return '';
    return isValidDateText(value) ? '' : '퇴사일자는 YYYY-MM-DD 형식이어야 합니다.';
  }
  if (key === 'email') {
    if (!value) return '';
    if (value.includes('@')) return /^[a-z0-9._-]+@gmail\.com$/.test(value) ? '' : 'gmail 주소만 허용됩니다.';
    return /^[a-z0-9._-]+$/.test(value) ? '' : '이메일 아이디 형식이 올바르지 않습니다.';
  }
  if (key === 'bank_account') {
    if (!value) return '';
    return /^\d+$/.test(value) ? '' : '계좌번호는 숫자만 입력 가능합니다.';
  }

  return '';
}

// 마케터 부서는 항상 '마케팅' — 서버(user.create.js:182, user.manage.js:340)가 강제하는 값과 같다.
//   마케팅명부 화면 폐지(2026-09-09)로 salesroster/helpers.js 가 사라져 여기로 옮겼다.
export const SALES_FIXED_DEPARTMENT = '마케팅';
